import { useState } from 'react'
import { DEFAULT_MARKET, MARKET_PRESETS, type MarketCode } from '@henlytics/types'
import { EntryGrid } from '../components/entry/EntryGrid'
import {
  FarmSettingsFields,
  draftFromPreset,
  inputFromDraft,
  type FarmSettingsDraft,
} from '../components/settings/FarmSettingsFields'
import { useCreateFarm } from '../hooks/useFarm'

const MARKETS = Object.keys(MARKET_PRESETS) as MarketCode[]

/**
 * First sign-in for a user who belongs to no farm yet.
 *
 * Pre-filled from a market preset, India by default. Nothing is saved until the
 * owner has checked every field and pressed the button: these settings shape
 * every figure the app shows.
 */
export function FarmSetup() {
  const create = useCreateFarm()
  const [market, setMarket] = useState<MarketCode>(DEFAULT_MARKET)
  const [draft, setDraft] = useState<FarmSettingsDraft>(() => draftFromPreset(DEFAULT_MARKET))

  const chooseMarket = (next: MarketCode) => {
    const preset = MARKET_PRESETS[next]
    setMarket(next)
    // Replace only the market's own fields; keep what the owner has typed.
    setDraft((current) => ({
      ...current,
      currencyCode: preset.currencyCode,
      timezone: preset.timezone,
      locale: preset.locale,
      feedUom: preset.feedUom,
    }))
  }

  return (
    <div className="page">
      <header className="page-head">
        <h2>Set up your farm</h2>
        <p className="muted">One-time setup. These settings shape every figure the app shows, so check each one.</p>
      </header>

      <EntryGrid
        title="Farm details"
        description="Pre-filled for India. The currency cannot be changed once money has been recorded."
        onSubmit={() => create.mutate(inputFromDraft(draft))}
        submitLabel="Create farm"
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
      >
        <label className="cell">
          <span className="cell-label">Market</span>
          <select
            className="cell-input"
            value={market}
            onChange={(event) => chooseMarket(event.target.value as MarketCode)}
          >
            {MARKETS.map((code) => (
              <option key={code} value={code}>
                {MARKET_PRESETS[code].label}
              </option>
            ))}
          </select>
          <span className="cell-hint">Fills in the fields below.</span>
        </label>
        <FarmSettingsFields
          value={draft}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        />
      </EntryGrid>
    </div>
  )
}
